import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  ReadyPlanOptionGroupPersonalizationType,
  CheckoutConfigOptionGroupStep as Step,
} from "src/generated/graphqlTypes";
import { pluralize } from "src/utils";
import {
  ReviewConfigurationStep_CheckoutConfigFragment,
  ReviewConfigurationStep_ConfigChangeFragment,
  ReviewConfigurationStep_OptionGroupFragment,
  ReviewConfigurationStep_ProgramDataFragment,
} from "./ReviewConfigurationStep.generated";

/**
 * A review configuration group is a titled row with its own amount, and the selections that make it up.
 * i.e.
 * title: Exterior
 * amountInCents: 100000
 * items: [
 *   { title: "B - Spanish", amountInCents: 100000 },
 *   { title: "Soapstone", amountInCents: 100000 },
 * ]
 */
export type ReviewConfigurationGroup = {
  title: string;
  amountInCents: number;
  items: { title: string; amountInCents?: number }[];
  /** Whether any of the row's selections differs from what the project had before the change event. */
  isChanged: boolean;
};
// Then the section is what is passed to the FormSectionLayout component to render the sections, i.e. Plan & Exterior, Interior, Add-Ons.
type ReviewConfigurationSection = { title: string; groups: ReviewConfigurationGroup[] };

/** The half of a CheckoutConfig a given structure is on. A config covers both the main house and the ADU. */
type ReviewConfigurationDetail = {
  changedGroupIds: ReadonlySet<string>;
  optionGroups: ReviewConfigurationStep_OptionGroupFragment[];
  /** What the config reserves each option at, keyed by ready plan option id. */
  priceByRpoId: ReadonlyMap<string, number>;
  /** What the config reserves that isn't an option, i.e. the plan itself. */
  basePriceInCents: number;
  productOffering: NamedFragment | null | undefined;
  programData: ReviewConfigurationStep_ProgramDataFragment | null | undefined;
};

/**
 * The ready plan option group ids whose selection the change event alters, i.e. the result side selects a different
 * option than the original side does (or one where the original had none). Keyed by the ready plan option group
 * rather than the checkout config's own group, since those differ between the two configs.
 *
 * Compares the configs themselves rather than `optionGroupChanges`, which Add/Modify Options does not populate.
 */
export function toChangedGroupIds(
  configChange: Pick<ReviewConfigurationStep_ConfigChangeFragment, "result" | "original">,
): Set<string> {
  const { result, original } = configChange;
  const originalOptionIdByGroupId = original.config.optionGroups
    .filter((og) => !!og.selectedOption)
    .keyByObject(
      (og) => og.group.id,
      (og) => og.selectedOption!.option.id,
    );
  return new Set(
    result.config.optionGroups
      .filter((og) => !!og.selectedOption && originalOptionIdByGroupId.get(og.group.id) !== og.selectedOption.option.id)
      .map((og) => og.group.id),
  );
}

/**
 * The sections of the structure's resulting configuration. Main house groups are sectioned by their step, ADU groups
 * (which all carry `step: ADU`) by their global option type, and empty sections are dropped.
 */
export function toReviewConfigurationSections(
  checkoutConfig: ReviewConfigurationStep_CheckoutConfigFragment,
  changedGroupIds: ReadonlySet<string> = new Set(),
): ReviewConfigurationSection[] {
  const { productOffering, programData, options, optionGroups, reservedTotalPriceInCents } = checkoutConfig.config;
  const { isReversed, interiorSubSteps } = checkoutConfig;
  // Priced off what the config reserves rather than live checkout pricing, so the rows add up to the total above them
  const priceByRpoId = options.keyByObject(
    (ccfgo) => ccfgo.readyPlanOption.id,
    (ccfgo) => ccfgo.reservedPriceInCents ?? 0,
  );
  const detail: ReviewConfigurationDetail = {
    changedGroupIds,
    productOffering,
    programData,
    priceByRpoId,
    basePriceInCents: reservedTotalPriceInCents - options.sum((ccfgo) => ccfgo.reservedPriceInCents ?? 0),
    optionGroups,
  };
  // Grouped by sub-step *number*, not step: the interior design package groups (Interior Scheme, Spec Level)
  // land on the first sub-step alongside the personalization ones. Mirrors `getInteriorPersonalizationGroupsBySubStep`.
  const bySubStep = selectedGroups(optionGroups)
    .filter((og) => !!og.subStepNumber)
    .groupBy((og) => og.subStepNumber!);
  const aduByCategory = selectedGroups(optionGroups, [Step.Adu]).groupBy(toAduCategory);
  const aduGroup = (category: AduCategory) => toGroup(category, aduByCategory[category] ?? [], detail);

  const sections: ReviewConfigurationSection[] = [
    {
      title: "Plan & Exterior",
      groups: [
        floorPlanGroup(detail, isReversed),
        toGroup("Exterior", selectedGroups(optionGroups, [Step.ExteriorElevation, Step.ExteriorDesignPackage]), detail),
        toGroup("Floor Plan Options", selectedGroups(optionGroups, [Step.OptionsStructural]), detail),
        aduGroup("Elevation"),
        aduGroup("Exterior Scheme"),
      ].compact(),
    },
    ...interiorSubSteps.map((subStep) => ({
      title: subStep.displayName,
      groups: (bySubStep[subStep.stepNumber] ?? []).map((og) => toGroup(og.group.name, [og], detail)).compact(),
    })),
    {
      title: "Add-Ons",
      // One row per add-on rather than one "Add-Ons" row with sub-lines, per the design. Add-ons repeat across
      // rooms, so the room is what tells them apart.
      groups: selectedGroups(optionGroups, [Step.OptionsAddon]).map((og) => {
        const lines = toSelectionLines(og, detail);
        const location = og.group.readyPlanLocation;
        const title = lines[0].title;
        return {
          title: location ? `${title} - ${location.marketingName}` : title,
          amountInCents: lines.sum((line) => line.amountInCents),
          items: [],
          isChanged: detail.changedGroupIds.has(og.group.id),
        };
      }),
    },
    { title: "Interior", groups: [aduGroup("Interior Scheme"), aduGroup("Spec Level")].compact() },
    {
      title: "Personalization",
      groups: (aduByCategory["Personalization"] ?? []).map((og) => toGroup(og.group.name, [og], detail)).compact(),
    },
  ];
  return sections.filter((section) => section.groups.nonEmpty);
}

/** One row: its title, the total of everything under it, and a line per selection at its own price. */
function toGroup(
  title: string,
  groups: ReviewConfigurationStep_OptionGroupFragment[],
  detail: ReviewConfigurationDetail,
): ReviewConfigurationGroup | undefined {
  if (groups.isEmpty) return undefined;
  const lines = groups.flatMap((og) => toSelectionLines(og, detail));
  return {
    title,
    amountInCents: lines.sum((line) => line.amountInCents),
    items: lines,
    isChanged: groups.some((og) => detail.changedGroupIds.has(og.group.id)),
  };
}

/** A group's selected option, plus its swatch child's when one rolls up into it (a swatch child is never its own row). */
function toSelectionLines(optionGroup: ReviewConfigurationStep_OptionGroupFragment, detail: ReviewConfigurationDetail) {
  const child = detail.optionGroups.find((og) => og.group.id === optionGroup.childGroup?.id);
  const rollsUp = !!child?.selectedOption && usesSwatchSelector(child);
  return (rollsUp ? [optionGroup, child!] : [optionGroup]).map((og) => ({
    title: og.selectedOption!.option.name,
    amountInCents: detail.priceByRpoId.get(og.selectedOption!.option.id) ?? 0,
  }));
}

/**
 * The plan itself, priced at whatever the side reserves beyond its options. A config the change event composed has no
 * starting price of its own, and for a customer's reserved config this is its sales price anyway. Its lines are
 * descriptive, so they carry no amount.
 */
function floorPlanGroup(detail: ReviewConfigurationDetail, isReversed: boolean): ReviewConfigurationGroup {
  const { productOffering, programData, basePriceInCents } = detail;
  return {
    title: "Floor Plan",
    amountInCents: basePriceInCents,
    isChanged: false,
    items: [
      productOffering && { title: `${productOffering.name} (Orientation: ${isReversed ? "Reversed" : "Standard"})` },
      { title: toProgramDataLine(programData) },
    ].compact(),
  };
}

/**
 * Option groups with a selection worth showing the customer, optionally narrowed to some steps.
 *
 * `showOnCustomerSummary`, not `isEnabled`: `isEnabled` flips false for personalization groups once a config is
 * reserved, which would silently drop real selections from the breakdown. Swatch children are dropped because they
 * are surfaced under their parent instead — `allGroups` still has them so `toSelectionLines` can find them.
 */
function selectedGroups(
  optionGroups: ReviewConfigurationStep_OptionGroupFragment[],
  steps?: Step[],
): ReviewConfigurationStep_OptionGroupFragment[] {
  return optionGroups.filter(
    (og) =>
      og.showOnCustomerSummary &&
      !!og.selectedOption &&
      !(og.parentGroup && usesSwatchSelector(og)) &&
      (!steps || steps.includes(og.step)),
  );
}

function usesSwatchSelector(optionGroup: ReviewConfigurationStep_OptionGroupFragment): boolean {
  const { personalizationType, displayPackageOptionsAsSingleProduct } = optionGroup.group;
  return (
    personalizationType?.code !== ReadyPlanOptionGroupPersonalizationType.Package ||
    !!displayPackageOptionsAsSingleProduct
  );
}

type AduCategory = "Elevation" | "Exterior Scheme" | "Interior Scheme" | "Spec Level" | "Personalization";

function toAduCategory(optionGroup: ReviewConfigurationStep_OptionGroupFragment): AduCategory {
  const { isElevation, isExteriorScheme, isInteriorScheme, isSpecLevel } = optionGroup.group.group.type;
  if (isElevation) return "Elevation";
  if (isExteriorScheme) return "Exterior Scheme";
  if (isInteriorScheme) return "Interior Scheme";
  if (isSpecLevel) return "Spec Level";
  return "Personalization";
}

/**
 * I.e. "5 Beds, 4.5 Baths & 4,250 Sq Ft". Half baths count as .5, and the decimal is dropped when it is a whole number.
 * Exported for testing.
 * */
export function toProgramDataLine(programData: ReviewConfigurationStep_ProgramDataFragment | null | undefined): string {
  const { bedrooms, fullBaths, halfBaths, sellableSqft } = programData ?? {};
  const bedCount = bedrooms ?? 0;
  const bathCount = (fullBaths ?? 0) + (halfBaths ?? 0) / 2;
  const baths = Number.isInteger(bathCount) ? bathCount : bathCount.toFixed(1);
  const sqft = (sellableSqft ?? 0).toLocaleString();
  return `${bedCount} ${pluralize(bedCount, "Bed")}, ${baths} ${pluralize(bathCount, "Bath")} & ${sqft} Sq Ft`;
}

/** The shape `appliedById` needs from a change, so it is not tied to Select Changes' fragment. */
type AppliableChange = {
  id: string;
  originalOption?: { readyPlanOption: { id: string } } | null;
  targetOption?: { readyPlanOption: { id: string } } | null;
};

/** Whether each change is applied, keyed by change id. A change is applied when `optionIds` holds its target option and not its original one. */
export function appliedById(changes: AppliableChange[], optionIds: readonly string[]): Map<string, boolean> {
  return new Map(
    changes.map((change) => {
      const targetId = change.targetOption?.readyPlanOption.id;
      const originalId = change.originalOption?.readyPlanOption.id;
      return [
        change.id,
        (!targetId || optionIds.includes(targetId)) && (!originalId || !optionIds.includes(originalId)),
      ];
    }),
  );
}
